import { Activity, Award, Headphones, Network, ShieldCheck, Star, ThumbsUp, type LucideIcon } from "lucide-react";

type VectorMarkProps = {
  className?: string;
};

const benefitIcons: LucideIcon[] = [Award, Activity, Network, Headphones];
const benefitGradients = [
  "icon-tile-teal",
  "icon-tile-indigo",
  "icon-tile-orange",
  "icon-tile-pink",
];

export const BenefitIcon = ({ index = 0, className = "mx-auto h-[40px] w-[40px]" }: VectorMarkProps & { index?: number }) => {
  const Icon = benefitIcons[index % benefitIcons.length];
  const gradient = benefitGradients[index % benefitGradients.length];

  return (
    <span className={`flex items-center justify-center rounded-[12px] ${gradient} ${className}`} aria-hidden="true">
      <Icon className="h-5 w-5" strokeWidth={2.3} />
    </span>
  );
};

export const ShieldTickMark = ({ className }: VectorMarkProps) => (
  <span className={`flex items-center justify-center rounded-[18px] icon-tile-teal ${className}`} aria-hidden="true">
    <ShieldCheck className="h-[55%] w-[55%]" strokeWidth={2.4} />
  </span>
);

export const SmsStarMark = ({ className }: VectorMarkProps) => (
  <span className={`flex items-center justify-center rounded-[14px] icon-tile-orange ${className}`} aria-hidden="true">
    <Star className="h-[52%] w-[52%] fill-primary-foreground" strokeWidth={2.2} />
  </span>
);

export const LikeMark = ({ className }: VectorMarkProps) => (
  <span className={`flex items-center justify-center rounded-full icon-tile-indigo ${className}`} aria-hidden="true">
    <ThumbsUp className="h-[50%] w-[50%]" strokeWidth={2.3} />
  </span>
);

export const YootelcoLogo = ({ className }: VectorMarkProps) => (
  <svg viewBox="0 0 190 46" className={className} role="img" aria-label="Yootelco">
    <path d="M6 9h56v28H6z" fill="currentColor" />
    <path d="M15 20h8l5 10l5-10h8L30 41h-8l3-6L15 20Z" fill="hsl(var(--card))" />
    <circle cx="47" cy="26" r="8" fill="hsl(var(--card))" />
    <circle cx="47" cy="26" r="3.5" fill="currentColor" />
    <text x="71" y="31" fill="currentColor" fontFamily="Arial, Helvetica, sans-serif" fontSize="30" fontWeight="800" letterSpacing="0">telco</text>
    <circle cx="101" cy="17" r="4" fill="hsl(var(--primary))" />
  </svg>
);

export const VectorWordmark = ({ name, className }: VectorMarkProps & { name: string }) => (
  <svg viewBox="0 0 180 54" className={className} role="img" aria-label={name}>
    <rect x="4" y="10" width="34" height="34" rx="8" fill="hsl(var(--primary))" />
    <path d="M14 28h14M21 21v14" stroke="hsl(var(--primary-foreground))" strokeWidth="4" strokeLinecap="round" />
    <text x="48" y="34" fill="currentColor" fontFamily="Arial, Helvetica, sans-serif" fontSize={name.length > 14 ? 16 : 20} fontWeight="800" letterSpacing="0">
      {name}
    </text>
  </svg>
);

export const CtaTopScribble = ({ className }: VectorMarkProps) => (
  <svg viewBox="0 0 128 76" className={className} fill="none" aria-hidden="true">
    <path d="M14 42C29 13 71 8 99 24C121 37 116 62 91 58C62 53 73 19 104 16" stroke="hsl(var(--sky))" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M19 57C38 42 63 40 88 48" stroke="hsl(var(--primary))" strokeWidth="5" strokeLinecap="round" opacity="0.65" />
    <path d="M37 18L45 9M48 24L60 18M22 28L12 22" stroke="hsl(var(--amber))" strokeWidth="5" strokeLinecap="round" />
  </svg>
);

export const CtaUnderlineScribble = ({ className }: VectorMarkProps) => (
  <svg viewBox="0 0 112 32" className={className} fill="none" aria-hidden="true">
    <path d="M7 18C21 7 38 6 52 16C66 27 86 28 105 12" stroke="hsl(var(--primary))" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12 24C35 30 67 29 96 21" stroke="hsl(var(--amber))" strokeWidth="4" strokeLinecap="round" opacity="0.75" />
  </svg>
);